'use client';

import { useState, useEffect, useLayoutEffect, useRef, useCallback, useId } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  AlertOctagon,
  Bell,
  BellDot,
  Info,
  Check,
  CheckCheck,
  Volume2,
  VolumeX,
  X,
} from 'lucide-react';
import { TERMS, plural, ALERT_HUD, SEVERITY_LABEL } from '@/lib/copy';
import { type AlarmEntry, sortActiveAlarms } from '@/components/dashboard/alarmTypes';
import { useOrgTimezone } from '@/hooks/useOrgTimezone';
import { formatClockWithZone } from '@/lib/dateFormat';
import type { AlertFeedState } from './useCopAlerts';

interface CopAlertBellProps {
  alerts: AlarmEntry[];
  /**
   * Whether `alerts` is an answer yet. An empty list means "nothing is wrong"
   * ONLY when this is `ok`; before that it means "we have not been told", and
   * on `failed` it means the feed is down. Painting those three the same way is
   * how an alert surface lies. See D21.
   */
  feedState: AlertFeedState;
  /** Epoch ms of the last successful poll, or `null` if none has succeeded.
   *  Read only under `failed`, to say how old the frozen reading is. */
  updatedAt?: number | null;
  /** Absent for a viewer, who may not acknowledge. The verb is then not drawn
   *  at all, rather than drawn and inert. D20. */
  onAcknowledge?: (id: string) => void;
  onResolve?: (id: string) => void;
  onFlyToDrone: (droneId: string) => void;
}

/** Above this many arriving at once, one summary toast replaces the individual
 *  ones. The complaint that started this work was alert churn stealing focus —
 *  a burst must not become a stack of toasts doing the same thing. */
const TOAST_BURST_LIMIT = 2;

/** The panel's width in CSS px. Kept in step with `w-80` on the panel itself;
 *  the placement arithmetic needs it as a number. */
const PANEL_WIDTH_PX = 320;

/** Gap kept between the panel and the viewport edge when it has to be clamped. */
const VIEWPORT_GAP_PX = 8;

type Severity = AlarmEntry['severity'];

/**
 * When an alert fired, in the org's own timezone with the zone named.
 *
 * Takes the zone rather than reading it: this is called from three places in one
 * render and a hook per call would be the same lookup three times.
 */
function formatAlarmTime(iso: string, timezone: string): string {
  try {
    // 'Z' keeps a UTC org byte-identical to what this panel has always
    // shown; the clock above uses the same helper with its own ' UTC'.
    return formatClockWithZone(new Date(iso), timezone, 'Z');
  } catch {
    return '--:--:--';
  }
}

function SeverityIcon({ severity, unacked }: { severity: string; unacked: boolean }) {
  const base = 'size-4 shrink-0';
  if (severity === 'critical') {
    return (
      <AlertOctagon
        className={cn(base, 'text-status-red-fg', unacked && 'animate-alarm-flash')}
        strokeWidth={2}
        aria-label={SEVERITY_LABEL.critical}
      />
    );
  }
  if (severity === 'warning') {
    return (
      <AlertTriangle
        className={cn(base, 'text-status-amber-fg', unacked && 'animate-alarm-flash')}
        strokeWidth={2}
        aria-label={SEVERITY_LABEL.warning}
      />
    );
  }
  return <Info className={cn(base, 'text-status-blue-fg')} strokeWidth={2} aria-label={SEVERITY_LABEL.info} />;
}

/** The colour the bell and its number are painted in.
 *
 *  Driven by the most severe UNACKNOWLEDGED alert, not the most severe standing
 *  one: the colour's job is to say how loudly this needs a person right now.
 *  Everything acknowledged and still standing is `--foreground` — visibly not
 *  nothing, visibly not shouting. See D14. */
export function bellTone(topUnacked: Severity | null, hasStanding: boolean): string {
  if (topUnacked === 'critical') return 'text-status-red-fg';
  if (topUnacked === 'warning') return 'text-status-amber-fg';
  if (topUnacked === 'info') return 'text-status-blue-fg';
  return hasStanding ? 'text-foreground' : 'text-muted-foreground';
}

/**
 * The COP's alert surface: a bell and a number in the top row, with the full
 * active list one press away.
 *
 * It was `CopAlarmHud`, a chip floating over the map's top-left corner, which
 * itself replaced an in-flow `AlarmPanel` row. That row rendered `null` at zero
 * alerts and 40 px at one, in the same flex column as the map — and `DroneMap`'s
 * ResizeObserver turns every such change into a real MapLibre canvas resize, so
 * an alert arriving moved the drone the operator was tracking.
 *
 * What actually protects the map now is that the HEADER's height is constant,
 * not that the header is a different element (D17): the header and the map
 * column are siblings in the same flex column, so a header that grew would go
 * straight into the map's box.
 *
 * That constant height USED to be set by something taller than this trigger, so
 * the trigger's own height did not matter. It does now: #530 moved the DAY /
 * NIGHT control out of the header and back to the sidebar, and with its 44 px
 * gone this 28 px bell is the tallest permanent child of the COP's `dense`
 * header. The `h-7` is therefore load-bearing rather than defensive — it is what
 * holds the map's box still, and a number appearing may only change the
 * trigger's WIDTH. Do not make this height depend on content. D25.
 */
export function CopAlertBell({
  alerts,
  feedState,
  updatedAt = null,
  onAcknowledge,
  onResolve,
  onFlyToDrone,
}: CopAlertBellProps) {
  const timezone = useOrgTimezone();
  const [open, setOpen] = useState(false);
  const [muted, setMuted] = useState(false);
  const [panelPos, setPanelPos] = useState<{ top: number; right: number } | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const oscillatorRef = useRef<OscillatorNode | null>(null);
  const toneTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const prevUnackedRef = useRef(0);
  /** Whether `prevUnackedRef` holds a real reading yet. Without it, 0 is
   *  indistinguishable from "not told", and the backlog reads as a rise. */
  const primedRef = useRef(false);
  /** Alert ids already announced. Seeded on the first LOADED list without
   *  toasting: the initial `/api/alerts` poll returns everything already
   *  standing, and announcing a backlog on every navigation is the noise this
   *  replaces. Seeding on the first RENDER instead — which is what this did
   *  until D21 — seeded an empty set every time, because the list is always
   *  `[]` until the fetch resolves. */
  const announcedRef = useRef<Set<string> | null>(null);
  const panelId = useId();

  const loaded = feedState === 'ok';
  const visibleAlerts = sortActiveAlarms(alerts);
  const unacked = visibleAlerts.filter((a) => a.status === 'active');
  const unackedCount = unacked.length;
  const criticalUnacked = unacked.filter((a) => a.severity === 'critical').length;
  /** `sortActiveAlarms` puts unacknowledged before acknowledged within a
   *  severity and severity first overall, so the head of that filtered list is
   *  already the most severe unacknowledged one. */
  const topUnacked: Severity | null = unacked[0]?.severity ?? null;
  const mostCritical = visibleAlerts[0] ?? null;
  const hasAlerts = visibleAlerts.length > 0;
  /** Standing, and every one of them already seen. Not nothing, not shouting. */
  const standingAcknowledged = hasAlerts && unackedCount === 0;

  // --- Audible alert (moved from CopAlarmHud) ----------------------------
  const startAlarmTone = useCallback(() => {
    if (muted) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new AudioContext();
      const ctx = audioCtxRef.current;
      if (oscillatorRef.current) return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.frequency.value = criticalUnacked > 0 ? 880 : 660;
      osc.type = criticalUnacked > 0 ? 'square' : 'sine';
      gain.gain.value = 0.08;
      osc.start();
      oscillatorRef.current = osc;
      toneTimerRef.current = setTimeout(() => {
        try { osc.stop(); } catch { /* already stopped */ }
        oscillatorRef.current = null;
      }, 800);
    } catch (err) {
      // Never silent: an audible alert that has stopped working is a
      // degradation an operator must be able to find out about. The commonest
      // cause is one leaked AudioContext per COP mount hitting the browser's
      // per-document cap — which the unmount cleanup below now prevents.
      console.warn('Alert tone unavailable', err);
    }
  }, [muted, criticalUnacked]);

  // Gated on `loaded` and primed on the first answer, for exactly the reason
  // the toast effect below is (D21). `prevUnackedRef` starting at 0 against a
  // list that starts `[]` meant the first poll's STANDING BACKLOG read as a
  // rise from nothing: an org with five standing alerts got the 880 Hz square
  // wave on every single entry into the COP, for alerts it had already seen.
  // The visual half of that was fixed and this, the louder half, was not —
  // and `muted` is component state, so it reset on every remount and the
  // operator could not make it stop.
  useEffect(() => {
    if (!primedRef.current) {
      // Nothing sounds until the feed has answered once. Only the PRIMING waits
      // on `loaded`, never the tone itself: gating the tone would mean a live
      // WebSocket alert arriving during an `/api/alerts` outage made no sound
      // at all, which trades one silence for a worse one.
      if (!loaded) return;
      primedRef.current = true;
      prevUnackedRef.current = unackedCount;
      return;
    }
    if (unackedCount > prevUnackedRef.current) {
      startAlarmTone();
    }
    prevUnackedRef.current = unackedCount;
  }, [loaded, unackedCount, startAlarmTone]);

  useEffect(() => {
    if (unackedCount === 0 && oscillatorRef.current) {
      try { oscillatorRef.current.stop(); } catch { /* already stopped */ }
      oscillatorRef.current = null;
    }
  }, [unackedCount]);

  useEffect(() => () => {
    clearTimeout(toneTimerRef.current);
    try { oscillatorRef.current?.stop(); } catch { /* already stopped */ }
    oscillatorRef.current = null;
    void audioCtxRef.current?.close().catch(() => { /* already closed */ });
    audioCtxRef.current = null;
  }, []);

  // --- Arrival toasts ----------------------------------------------------
  // The bell carries the standing state; a toast only says "this one is new",
  // because a number that merely increments is easy to miss.
  useEffect(() => {
    if (!loaded) return;
    const active = alerts.filter((a) => a.status === 'active');

    if (announcedRef.current === null) {
      announcedRef.current = new Set(active.map((a) => a.id));
      return;
    }

    const seen = announcedRef.current;
    const arrived = active.filter((a) => !seen.has(a.id));
    for (const a of active) seen.add(a.id);
    // Let ids of alerts that are no longer active fall out, so a re-raised
    // alert announces itself again rather than being silently swallowed.
    const activeIds = new Set(active.map((a) => a.id));
    for (const id of seen) {
      if (!activeIds.has(id)) seen.delete(id);
    }

    if (arrived.length === 0) return;

    if (arrived.length > TOAST_BURST_LIMIT) {
      // Was `${n} NEW ALERTS`, which shouted and read "1 NEW ALERTS" at a burst of one.
      toast.warning(ALERT_HUD.burstToast(arrived.length), {
        description: ALERT_HUD.burstDescription,
        action: { label: ALERT_HUD.openList, onClick: () => setOpen(true) },
      });
      return;
    }

    for (const a of arrived) {
      const notify =
        a.severity === 'critical' ? toast.error : a.severity === 'warning' ? toast.warning : toast.info;
      // The severity is a word, not `a.severity.toUpperCase()`: that put the database's own
      // column value on screen, shouted (#505 T8, rules 4 and 5). Only critical keeps capitals.
      notify(ALERT_HUD.toastTitle({ severity: SEVERITY_LABEL[a.severity], title: a.title }), {
        description: ALERT_HUD.toastDescription({
          callsign: a.drone_callsign ?? null,
          message: a.message,
        }),
        duration: a.severity === 'critical' ? 10_000 : 6_000,
        action: onAcknowledge
          ? { label: ALERT_HUD.acknowledge, onClick: () => onAcknowledge(a.id) }
          : undefined,
      });
    }
  }, [alerts, loaded, onAcknowledge]);

  const close = useCallback(() => {
    setOpen(false);
    // Escape and an outside press both left focus on `document.body`, so the
    // next Tab restarted from the top of the page. Hand it back to the control
    // the operator opened this from.
    triggerRef.current?.focus();
  }, []);

  // --- The panel is a dialog, and closes like every other one on the COP ---
  //
  // `window` + capture with `stopPropagation`, and `role="dialog"` on the panel,
  // are not stylistic choices: `page.tsx` owns Escape on window/capture and
  // swallows it (disarming the map mode) unless a `[role="dialog"]` is on
  // screen, and `DroneWorkspace` and `CopMissionsPanel` close themselves on the
  // same key with the same guard. On `document`/bubble this handler would never
  // run with a map mode armed, and would close two surfaces at once without one.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      close();
    };
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('mousedown', onPointerDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown);
    };
  }, [open, close]);

  // --- Placement ---------------------------------------------------------
  // The panel is `fixed` and measured rather than `absolute right-0`, because
  // the bell is the FIRST child of the header's right cluster: the dots, the
  // DAY / NIGHT control, the clock and the health word all sit to its right, so
  // its own right edge is several hundred px in from the viewport's. Anchored to
  // the bell, a 320 px panel starts off-screen on a narrow display and is
  // clipped by `<main>`'s `overflow-hidden` rather than scrolled to.
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const fromBell = window.innerWidth - rect.right;
      const widest = window.innerWidth - PANEL_WIDTH_PX - VIEWPORT_GAP_PX;
      setPanelPos({
        top: rect.bottom + 6,
        right: Math.max(VIEWPORT_GAP_PX, Math.min(fromBell, widest)),
      });
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  const tone = bellTone(topUnacked, hasAlerts);
  /** The feed has failed and there is still a list on screen. The reading is
   *  the last good one, frozen: it must not be drawn as though it were live.
   *  The `--` branch below only covers the failed-AND-empty case, which is the
   *  one that matters least. D21's rule applies to a stale list too. */
  const stale = feedState === 'failed' && hasAlerts;
  const staleAge = stale && updatedAt !== null ? `, last updated ${formatAlarmTime(new Date(updatedAt).toISOString(), timezone)}` : '';
  /**
   * The state in words, which is the ONLY channel that survives everything.
   *
   * The severity leads, because it is the reading an operator needs first and
   * because the visual channel carrying it is thin: one bell glyph separates
   * critical from warning by `#FF7A73` against `#EFA831`, the classic red/amber
   * confusion pair, plus a flash that `prefers-reduced-motion` removes outright
   * (`globals.css:590-596`). D18 said this string was what compensated for
   * that; it did not name the severity at all. It does now, and it reaches the
   * accessible name, the native tooltip and the live region from here.
   */
  const summary = hasAlerts && mostCritical
    ? `${SEVERITY_LABEL[topUnacked ?? mostCritical.severity]} - ${plural(visibleAlerts.length, `active ${TERMS.alert}`, `active ${TERMS.alerts}`)}, ${unackedCount} unacknowledged${stale ? `. ${ALERT_HUD.feedUnavailable}${staleAge}` : ''}`
    : loaded
      ? ALERT_HUD.none
      : feedState === 'failed'
        ? ALERT_HUD.feedUnavailable
        : ALERT_HUD.loading;

  return (
    <div ref={rootRef} className="relative">
      {/* The chip this replaced was an always-mounted overlay carrying the
          standing state for a screen reader. The panel below is only in the DOM
          while open, so the announcement lives here instead — otherwise moving
          the surface into the header would silently cost that. It is also the
          ONLY live region here: the list itself is not a `role="log"`, or an
          arriving alert would be announced twice while the panel is open. */}
      <span className="sr-only" role="status" aria-live="polite">
        {hasAlerts && mostCritical
          ? ALERT_HUD.summary({
              unacked: unackedCount,
              total: visibleAlerts.length,
              severity: SEVERITY_LABEL[mostCritical.severity],
              title: mostCritical.title,
            })
          : `${summary}.`}
      </span>

      {/* Bell and number, nothing else. Rendered at zero alerts and at every
          breakpoint, so the operator always knows where to look (ISA-18.2 wants
          the alert indication present on every screen, not conjured on arrival)
          and so its arrival never moves the header. */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-controls={open ? panelId : undefined}
        aria-label={summary}
        title={summary}
        className={cn(
          'flex h-7 min-w-7 items-center justify-center gap-1.5 rounded px-1 cursor-pointer transition-colors hover:bg-accent/10',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring',
          // Critical gets a second, STATIC channel. The flash is the only thing
          // that separated it from warning beyond hue, and `prefers-reduced-
          // motion` turns the flash off entirely — leaving an operator who
          // asked for reduced motion, or who cannot separate red from amber,
          // with no difference at all. A ring is a box-shadow, so this cannot
          // change the trigger's height and D25 still holds.
          criticalUnacked > 0 && 'bg-status-red-tint ring-1 ring-status-red-fg/60',
          tone,
        )}
      >
        {standingAcknowledged ? (
          // A different SHAPE, not a different hue: acknowledged-and-standing
          // has to be told apart from all-clear by an operator who cannot
          // separate the two colours. D19.
          <BellDot className="size-4 shrink-0" strokeWidth={2} />
        ) : (
          <Bell
            className={cn('size-4 shrink-0', criticalUnacked > 0 && 'animate-alarm-flash')}
            strokeWidth={2}
          />
        )}
        {!loaded && unackedCount === 0 ? (
          // Not a number, on purpose: the repo's convention for a reading the
          // system does not have (see `UtcClock`). An empty bell here would
          // claim all-clear on a dead feed. A live WebSocket alert arriving
          // before the first poll settles is data, so it takes the count branch
          // rather than this one.
          //
          // This covers "the feed is down and everything standing is already
          // acknowledged" too, which drew nothing at all before: with a dead
          // feed we cannot say that is still true.
          <span className="font-mono tabular-nums text-[13px] font-bold leading-none">--</span>
        ) : unackedCount > 0 ? (
          <span
            className={cn(
              'font-mono tabular-nums text-[13px] font-bold leading-none',
              // A frozen count, struck rather than hidden: stale data beats no
              // data on an alert surface, but it must not be readable as live.
              stale && 'line-through decoration-2 opacity-70',
            )}
          >
            {unackedCount}
          </span>
        ) : null}
      </button>

      {/* The panel. Opens on `open` alone — never on `open && hasAlerts`, which
          left the bell a dead control at zero and then sprang the panel open by
          itself when an alert eventually arrived. */}
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label={ALERT_HUD.listLabel}
          className="fixed z-50 w-80 max-w-[calc(100vw-1rem)] overflow-hidden rounded-md border border-subtle bg-card/95 shadow-xl backdrop-blur-sm"
          style={{
            top: panelPos?.top ?? 56,
            right: panelPos?.right ?? VIEWPORT_GAP_PX,
          }}
        >
          <div className="flex items-center justify-between gap-2 border-b border-subtle px-3 py-2">
            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
              Active {TERMS.alerts}
            </span>
            <div className="flex items-center gap-1">
              {/* Mute lives here rather than beside the bell: the bell is a bell
                  and a number (D15). This is where an operator looks when the
                  thing making the noise is what they just opened. */}
              <button
                type="button"
                onClick={() => setMuted((v) => !v)}
                className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                aria-label={muted ? ALERT_HUD.unmute : ALERT_HUD.mute}
                title={muted ? ALERT_HUD.unmute : ALERT_HUD.mute}
              >
                {muted ? <VolumeX className="size-3.5" /> : <Volume2 className="size-3.5" />}
              </button>
              <button
                type="button"
                onClick={close}
                className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                aria-label={ALERT_HUD.closeList}
                title={ALERT_HUD.closeList}
              >
                <X className="size-3.5" strokeWidth={2} />
              </button>
            </div>
          </div>

          {/* Unconditional on the list being empty, unlike the paragraph below.
              The failure that matters is a dead feed with alerts still on
              screen: the list keeps its last good rows, their timestamps, and
              live verbs, and without this nothing anywhere says the reading
              stopped moving. */}
          {stale && (
            <p
              role="status"
              className="border-b border-subtle bg-status-amber-tint px-3 py-1.5 text-[11px] text-status-amber-fg"
            >
              {ALERT_HUD.feedUnavailableRetrying}
              {updatedAt !== null
                ? ` Showing the last reading from ${formatAlarmTime(new Date(updatedAt).toISOString(), timezone)}.`
                : ''}
            </p>
          )}

          {!hasAlerts ? (
            <p className="px-3 py-4 text-center text-[12px] text-muted-foreground">
              {loaded
                ? `${ALERT_HUD.none}.`
                : feedState === 'failed'
                  ? ALERT_HUD.feedUnavailableRetrying
                  : `${ALERT_HUD.loading}…`}
            </p>
          ) : (
            <div className="max-h-[280px] overflow-y-auto divide-y divide-black/5 dark:divide-white/5">
              {visibleAlerts.map((alarm) => {
                const isUnacked = alarm.status === 'active';
                return (
                  <div
                    key={alarm.id}
                    className={cn(
                      'flex flex-col gap-1.5 px-3 py-2 text-[13px]',
                      alarm.severity === 'critical' && isUnacked && 'bg-alarm-critical-unacked',
                      alarm.severity === 'critical' && !isUnacked && 'bg-alarm-critical-acked',
                      alarm.severity === 'warning' && isUnacked && 'bg-alarm-warning-unacked',
                      alarm.severity === 'warning' && !isUnacked && 'bg-alarm-warning-acked',
                      alarm.severity === 'info' && 'bg-alarm-info',
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <SeverityIcon severity={alarm.severity} unacked={isUnacked} />
                      <span className="text-[11px] text-muted-foreground font-mono tabular-nums shrink-0">
                        {formatAlarmTime(alarm.created_at, timezone)}
                      </span>
                      {alarm.drone_callsign && alarm.drone_id && (
                        <button
                          type="button"
                          onClick={() => onFlyToDrone(alarm.drone_id!)}
                          className="uppercase font-bold text-[11px] text-status-blue-fg underline underline-offset-2 hover:opacity-80 shrink-0 tracking-wider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                          title={ALERT_HUD.flyTo}
                        >
                          {alarm.drone_callsign}
                        </button>
                      )}
                    </div>

                    <div className="flex items-center gap-2 min-w-0">
                      <span className="flex-1 truncate text-foreground" title={alarm.message}>
                        {alarm.title}
                      </span>
                      {/* Absent for a viewer. Drawn-and-inert was the old
                          behaviour: the press did nothing and said nothing. */}
                      {isUnacked && onAcknowledge && (
                        <button
                          type="button"
                          onClick={() => onAcknowledge(alarm.id)}
                          className="shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-hover text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground uppercase tracking-wider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        >
                          <Check className="size-3" strokeWidth={2} />
                          Acknowledge
                        </button>
                      )}
                      {!isUnacked && onResolve && (
                        <button
                          type="button"
                          onClick={() => onResolve(alarm.id)}
                          className="shrink-0 flex items-center gap-1 px-2 py-1 rounded border border-status-green-fg/40 text-[11px] text-status-green-fg hover:bg-status-green/15 uppercase tracking-wider focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        >
                          <CheckCheck className="size-3" strokeWidth={2} />
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
